/**
 * Value-level semantics: loose equality, SQL-ish ordering, and LIKE patterns.
 *
 * The snapshot is JSON, so a column that is `numeric` in Postgres can arrive as
 * a number while the same value arrives as a string through `.or()` (which is
 * parsed out of a URL-ish filter string). Comparisons therefore coerce.
 */

/** `null` and `undefined` both mean SQL NULL here — snapshots omit null columns. */
export function isNullish(v: unknown): boolean {
  return v === null || v === undefined;
}

function toNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const s = v.trim();
    if (s === '') return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * `a = b` with cross-type tolerance (number vs numeric string, boolean vs
 * `'true'`). Objects/arrays fall back to structural equality.
 */
export function looseEq(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (isNullish(a) || isNullish(b)) return false;
  if (typeof a === 'object' || typeof b === 'object') {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }
  return String(a) === String(b);
}

/**
 * Total order used by `.order()` and by `gt/gte/lt/lte`.
 * Numbers compare numerically, booleans false < true, everything else compares
 * as strings — which is correct for ISO-8601 timestamps and for job numbers.
 */
export function compareValues(a: unknown, b: unknown): number {
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return (a ? 1 : 0) - (b ? 1 : 0);
  }
  const na = toNumber(a);
  const nb = toNumber(b);
  if (na !== null && nb !== null) return na < nb ? -1 : na > nb ? 1 : 0;
  const sa = String(a);
  const sb = String(b);
  return sa < sb ? -1 : sa > sb ? 1 : 0;
}

const likeCache = new Map<string, RegExp>();

/**
 * PostgREST LIKE patterns. Postgres uses `%` (any run) and `_` (one char);
 * PostgREST additionally accepts `*` as an alias for `%` inside a filter
 * string, and this app uses BOTH (`job_number.like.26-1273-*` from the family
 * lookup, `%smith%` from search boxes). Support both.
 */
export function likeToRegExp(pattern: string, caseInsensitive: boolean): RegExp {
  const key = `${caseInsensitive ? 'i' : ''} ${pattern}`;
  const hit = likeCache.get(key);
  if (hit) return hit;
  let body = '';
  for (const ch of pattern) {
    if (ch === '%' || ch === '*') body += '[^]*';
    else if (ch === '_') body += '[^]';
    else body += ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  const re = new RegExp(`^${body}$`, caseInsensitive ? 'i' : '');
  likeCache.set(key, re);
  return re;
}

export function likeMatch(value: unknown, pattern: unknown, caseInsensitive: boolean): boolean {
  if (isNullish(value)) return false;
  return likeToRegExp(String(pattern), caseInsensitive).test(String(value));
}

/** `col.is.<null|true|false>` — never NULL itself, so it is safe to negate. */
export function isMatch(value: unknown, target: unknown): boolean {
  if (isNullish(target) || target === 'null') return isNullish(value);
  if (target === true || target === 'true') return value === true;
  if (target === false || target === 'false') return value === false;
  return looseEq(value, target);
}

/** `@>` for `text[]` columns (and single-value shorthand). */
export function arrayContains(value: unknown, target: unknown): boolean {
  if (!Array.isArray(value)) return false;
  const wanted = Array.isArray(target) ? target : [target];
  return wanted.every((w) => value.some((v) => looseEq(v, w)));
}

/** `&&` for `text[]` columns. */
export function arrayOverlaps(value: unknown, target: unknown): boolean {
  if (!Array.isArray(value)) return false;
  const wanted = Array.isArray(target) ? target : [target];
  return wanted.some((v0) => value.some((v) => looseEq(v, v0)));
}
